import { FormattedMessage, useIntl } from 'react-intl';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { useTitleStore } from 'src/stores/useTitleStore';
import * as React from 'react';
import { Button } from 'src/registry/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICON_MAP } from 'src/utils/constants';
import { SectionBlock, SectionBlockHeader, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import { Badge } from 'src/registry/ui/Badge';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { commonMessages, generalMessages } from 'src/utils/messages';
import DashboardGeneralBlock from 'src/features/billing/components/DashboardGeneralBlock';
import { paymentsMessages } from 'src/features/payments/utils/messages';
import {
    FINANCIAL_DOCUMENT_PAYMENT_STATUS_BADGE_OPTIONS,
    FINANCIAL_DOCUMENT_STATUS_BADGE_OPTIONS,
    FINANCIAL_DOCUMENT_TYPE_BADGE_OPTIONS,
} from 'src/features/financialDocuments/utils/constants';
import { CellCurrencyBadge } from 'src/features/moneyAccounts/components/tables/CellCurrencyBadge';
import Tab from 'src/registry/components/navigation/Tab';
import TabRow from 'src/registry/components/navigation/TabRow';
import { paymentAllocationMessages } from 'src/features/paymentAllocations/utils/messages';
import { financialDocumentsMessages } from 'src/features/financialDocuments/utils/messages';
import { useGetFinancialDocument } from 'src/features/financialDocuments/api/useGetFinancialDocument';
import { financialDocumentRowMessages } from 'src/features/financialDocumentRows/utils/messages';
import { accountingHeadersMessages } from 'src/features/accountingHeaders/utils/messages';
import {
    FINANCIAL_DOCUMENT_PAYMENT_STATUS,
    FINANCIAL_DOCUMENT_STATUS,
} from 'src/features/financialDocuments/utils/types';
import { getFormattedDateTime } from 'src/utils/getFormattedDateTime';
import FinancialDocumentDashboardActionButtons from 'src/features/financialDocuments/components/FinancialDocumentDashboardActionButtons';
import { accountingRangesMessages } from 'src/features/accountingRanges/utils/messages';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { LinkWithIcon } from 'src/registry/components/navigation/LinkWithIcon';
import { FinancialDocumentTabContext } from 'src/features/financialDocuments/utils/tabContext';

const FinancialDocumentDashboard = () => {
    const { formatMessage, formatNumber } = useIntl();
    const navigate = useNavigate();
    const { financialDocumentId = '' } = useParams();

    const permissionAccessPayments = useHasPermissions([PERMISSION.ACCESS_PAYMENTS]);
    const permissionAccessCustomers = useHasPermissions([PERMISSION.ACCESS_CUSTOMERS]);
    const permissionAccessBillingEntities = useHasPermissions([PERMISSION.ACCESS_BILLING_ENTITIES]);
    const permissionAccessAccountingRanges = useHasPermissions([PERMISSION.ACCESS_ACCOUNTING_RANGES]);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(financialDocumentsMessages.financialDocumentDashboard));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { data: financialDocument } = useGetFinancialDocument({
        documentId: financialDocumentId,
    });

    const formattedCreatedAtDate = getFormattedDateTime(financialDocument?.created_at)?.formattedDate;
    const sectionTitle = financialDocument?.number
        ? financialDocument?.number
        : `${formatMessage(financialDocumentsMessages.draft)} ${formattedCreatedAtDate}`;

    const dashboardBlockData = {
        section1: [
            {
                title: financialDocumentsMessages.financialDocCustomer,
                value: financialDocument?.customer && (
                    <LinkWithIcon
                        to={`/customers/${financialDocument?.customer.id}`}
                        linkTitle={financialDocument?.customer.name}
                        hasPermissions={permissionAccessCustomers}
                    />
                ),
            },
            {
                title: financialDocumentsMessages.financialDocBillingEntity,
                value: financialDocument?.supplier && (
                    <LinkWithIcon
                        to={`/billing-entity/${financialDocument?.supplier.id}`}
                        linkTitle={financialDocument?.supplier.name}
                        hasPermissions={permissionAccessBillingEntities}
                    />
                ),
            },
            {
                title: accountingHeadersMessages.parent,
                value: financialDocument?.financial_document && (
                    <LinkWithIcon
                        to={`/financial-documents/${financialDocument?.financial_document?.id}`}
                        linkTitle={financialDocument?.financial_document?.number}
                        hasPermissions={true}
                    />
                ),
            },
            {
                title: paymentsMessages.type,
                value: financialDocument ? (
                    <Badge theme={FINANCIAL_DOCUMENT_TYPE_BADGE_OPTIONS[financialDocument.type].theme || 'gray'}>
                        <FontAwesomeIcon
                            icon={FINANCIAL_DOCUMENT_TYPE_BADGE_OPTIONS[financialDocument.type].icon as IconProp}
                        />
                        {formatMessage(FINANCIAL_DOCUMENT_TYPE_BADGE_OPTIONS[financialDocument.type].label)}
                    </Badge>
                ) : (
                    '-'
                ),
            },
            {
                title: generalMessages.status,
                value: financialDocument?.status ? (
                    <Badge theme={FINANCIAL_DOCUMENT_STATUS_BADGE_OPTIONS[financialDocument.status].theme || 'gray'}>
                        <FontAwesomeIcon
                            icon={FINANCIAL_DOCUMENT_STATUS_BADGE_OPTIONS[financialDocument.status].icon as IconProp}
                        />
                        {formatMessage(FINANCIAL_DOCUMENT_STATUS_BADGE_OPTIONS[financialDocument.status].label)}
                    </Badge>
                ) : (
                    '-'
                ),
            },
            {
                title: paymentsMessages.paymentStatus,
                value: financialDocument?.payment_status ? (
                    <Badge
                        theme={
                            FINANCIAL_DOCUMENT_PAYMENT_STATUS_BADGE_OPTIONS[financialDocument.payment_status].theme ||
                            'gray'
                        }
                    >
                        <FontAwesomeIcon
                            icon={
                                FINANCIAL_DOCUMENT_PAYMENT_STATUS_BADGE_OPTIONS[financialDocument.payment_status]
                                    .icon as IconProp
                            }
                        />
                        {formatMessage(
                            FINANCIAL_DOCUMENT_PAYMENT_STATUS_BADGE_OPTIONS[financialDocument.payment_status].label,
                        )}
                    </Badge>
                ) : (
                    '-'
                ),
            },
            {
                title: accountingRangesMessages.accountingRange,
                value: financialDocument?.accounting_range && (
                    <LinkWithIcon
                        to={`/accounting-ranges/${financialDocument.accounting_range.id}`}
                        linkTitle={financialDocument?.accounting_range?.name}
                        hasPermissions={permissionAccessAccountingRanges}
                    />
                ),
            },
        ],
        section2: [
            {
                title: financialDocumentsMessages.createdAt,
                value: getFormattedDateTime(financialDocument?.created_at)?.formattedDate ?? '-',
            },
            {
                title: financialDocumentsMessages.issueDate,
                value: financialDocument?.issue_due_date
                    ? getFormattedDateTime(financialDocument?.issue_due_date)?.formattedDate
                    : '-',
            },
            {
                title: financialDocumentsMessages.issuedBy,
                value: financialDocument?.issued_by?.name ?? '-',
            },

            {
                title: financialDocumentsMessages.paymentDueDate,
                value: financialDocument?.payment_due_date
                    ? getFormattedDateTime(financialDocument?.payment_due_date)?.formattedDate
                    : '-',
            },
            {
                title: financialDocumentsMessages.taxEventDate,
                value: financialDocument?.tax_event_date
                    ? getFormattedDateTime(financialDocument?.tax_event_date)?.formattedDate
                    : '-',
            },
        ],
        section3: [
            {
                title: commonMessages.currency,
                value: financialDocument?.currency ? <CellCurrencyBadge currency={financialDocument?.currency} /> : '-',
            },
            {
                title: paymentsMessages.amount,
                value:
                    financialDocument?.amount != null &&
                    formatNumber(financialDocument.amount / 100, {
                        style: 'currency',
                        currency: financialDocument.currency,
                    }),
            },
            {
                title: financialDocumentsMessages.amountWithTax,
                value:
                    financialDocument?.amount_with_tax != null &&
                    formatNumber(financialDocument.amount_with_tax / 100, {
                        style: 'currency',
                        currency: financialDocument.currency,
                    }),
            },
            {
                title: financialDocumentsMessages.taxAmount,
                value:
                    financialDocument?.tax_amount != null
                        ? formatNumber(financialDocument.tax_amount / 100, {
                              style: 'currency',
                              currency: financialDocument.currency,
                          })
                        : '-',
            },
            {
                title: financialDocumentsMessages.paidAmount,
                value:
                    financialDocument?.paid_amount != null &&
                    formatNumber(financialDocument.paid_amount / 100, {
                        style: 'currency',
                        currency: financialDocument.currency,
                    }),
            },
            {
                title: paymentsMessages.remainingAmount,
                value:
                    financialDocument?.remaining_amount != null &&
                    formatNumber(financialDocument.remaining_amount / 100, {
                        style: 'currency',
                        currency: financialDocument.currency,
                    }),
            },
        ],
        section4: [
            {
                title: financialDocumentsMessages.publicDescription,
                value: financialDocument?.public_description ?? '-',
            },
            {
                title: financialDocumentsMessages.internalDescription,
                value: financialDocument?.internal_description ?? '-',
            },
        ],
    };

    return (
        <>
            <Button
                asChild
                className="place-self-start p-0"
                variant="link"
                onClick={() => navigate('/financial-documents')}
            >
                <div className="flex cursor-pointer">
                    <FontAwesomeIcon icon={ICON_MAP.BACK} className="opacity-50" />
                    {formatMessage(financialDocumentsMessages.backToAllFinancialDocuments)}
                </div>
            </Button>

            <div className="mt-6 grid grid-cols-1 gap-8">
                <SectionBlock>
                    <SectionBlockHeader>
                        <SectionBlockTitle className="text-2xl whitespace-nowrap">{sectionTitle}</SectionBlockTitle>

                        <FinancialDocumentDashboardActionButtons financialDocument={financialDocument} />
                    </SectionBlockHeader>
                    <DashboardGeneralBlock dashboardData={dashboardBlockData} indexForLastSeparator={3} />
                </SectionBlock>

                <div className="grid flex-3 grid-cols-1">
                    <TabRow>
                        <Tab to="rows" preventScrollReset>
                            <FormattedMessage {...financialDocumentRowMessages.rowsTitle} />
                        </Tab>
                        {permissionAccessPayments && (
                            <Tab to="allocations" preventScrollReset>
                                <FormattedMessage {...paymentAllocationMessages.allocations} />
                            </Tab>
                        )}
                    </TabRow>
                    <div className="mt-4">
                        <Outlet
                            context={
                                {
                                    documentStatus: financialDocument?.status,
                                    currency: financialDocument?.currency,
                                    disableAllocationCreate:
                                        financialDocument?.status !== FINANCIAL_DOCUMENT_STATUS.ISSUED ||
                                        financialDocument.payment_status === FINANCIAL_DOCUMENT_PAYMENT_STATUS.PAID,
                                } satisfies FinancialDocumentTabContext
                            }
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default FinancialDocumentDashboard;
